import type { OwnerScope } from '@prisma/client';
import { z } from 'zod';

export const SCRIPT_OWNER_SCOPES = ['MEMBER', 'HUB', 'SYSTEM'] as const;
export type ScriptOwnerScopeValue = (typeof SCRIPT_OWNER_SCOPES)[number];
export const SCRIPT_OWNER_SCOPES_MATCH_PRISMA: [OwnerScope] extends [ScriptOwnerScopeValue] ? true : never = true;

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([z.null(), z.boolean(), z.number(), z.string(), z.array(jsonValueSchema), z.record(jsonValueSchema)])
);

export const SCRIPT_MEMORY_LIMIT_BYTES = 64 * 1024 * 1024;
export const SCRIPT_CPU_LIMIT_MS = 2_000;
export const SCRIPT_INPUT_MAX_BYTES = 1_048_576;
export const SCRIPT_OUTPUT_MAX_BYTES = 262_144;
export const SCRIPT_LOG_MAX_LINES = 200;
export const SCRIPT_LOG_MAX_BYTES = 10_240;

export interface ScriptRunError {
  name: string;
  message: string;
}

export type ScriptRunResult =
  | { ok: true; output: unknown; logs: string[]; durationMs: number }
  | { ok: false; error: ScriptRunError; logs: string[]; durationMs: number };

export const scriptIdSchema = z.number().int().positive();
export const scriptNameSchema = z.string().trim().min(1).max(200);
export const scriptDescriptionSchema = z.string().trim().max(2000).nullable();
export const scriptBodySchema = z.string().min(1).max(100_000);
export const scriptOwnerTypeSchema = z.enum(['HUB', 'MEMBER']);

export interface ScriptSafe {
  id: number;
  ownerScope: OwnerScope;
  name: string;
  description: string | null;
  body: string;
  inputExample: JsonValue | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export const scriptSafeSchema = z.object({
  id: scriptIdSchema,
  ownerScope: z.enum(SCRIPT_OWNER_SCOPES),
  name: z.string(),
  description: z.string().nullable(),
  body: z.string(),
  inputExample: jsonValueSchema.nullable(),
  isEnabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export interface ScriptSummary {
  id: number;
  ownerScope: OwnerScope;
  name: string;
  description: string | null;
  isEnabled: boolean;
  hasInputExample: boolean;
  updatedAt: string;
}

export const scriptSummarySchema = z.object({
  id: scriptIdSchema,
  ownerScope: z.enum(SCRIPT_OWNER_SCOPES),
  name: z.string(),
  description: z.string().nullable(),
  isEnabled: z.boolean(),
  hasInputExample: z.boolean(),
  updatedAt: z.string(),
});

export const scriptListInputSchema = z.object({}).strict();

export interface ScriptListResult {
  items: ScriptSummary[];
}

export const scriptListOutputSchema = z.object({ items: z.array(scriptSummarySchema) });

export const scriptReadInputSchema = z.object({ scriptId: scriptIdSchema }).strict();
export type ScriptReadInput = z.infer<typeof scriptReadInputSchema>;

export const scriptCreateInputSchema = z
  .object({
    ownerType: scriptOwnerTypeSchema.default('MEMBER'),
    name: scriptNameSchema,
    description: scriptDescriptionSchema.optional(),
    body: scriptBodySchema,
    inputExample: jsonValueSchema.nullable().optional(),
    isEnabled: z.boolean().optional(),
  })
  .strict();
export type ScriptCreateInput = z.infer<typeof scriptCreateInputSchema>;
export type ScriptCreateBody = z.input<typeof scriptCreateInputSchema>;

export interface ScriptWriteResult extends ScriptSafe {
  url: string;
}

export const scriptWriteOutputSchema = scriptSafeSchema.extend({ url: z.string().min(1) });

const scriptUpdateBaseSchema = z
  .object({
    scriptId: scriptIdSchema,
    name: scriptNameSchema.optional(),
    description: scriptDescriptionSchema.optional(),
    body: scriptBodySchema.optional(),
    inputExample: jsonValueSchema.nullable().optional(),
    isEnabled: z.boolean().optional(),
  })
  .strict();

const requireSomeScriptField = (value: {
  name?: string;
  description?: string | null;
  body?: string;
  inputExample?: JsonValue | null;
  isEnabled?: boolean;
}): boolean =>
  value.name !== undefined ||
  value.description !== undefined ||
  value.body !== undefined ||
  value.inputExample !== undefined ||
  value.isEnabled !== undefined;
const requireSomeScriptFieldOptions = { message: 'Provide at least one field to update' };

export const scriptUpdateInputSchema = scriptUpdateBaseSchema.refine(requireSomeScriptField, requireSomeScriptFieldOptions);
export type ScriptUpdateInput = z.infer<typeof scriptUpdateInputSchema>;
export const scriptUpdateBodySchema = scriptUpdateBaseSchema
  .omit({ scriptId: true })
  .refine(requireSomeScriptField, requireSomeScriptFieldOptions);
export type ScriptUpdateBody = z.input<typeof scriptUpdateBodySchema>;

export const scriptDeleteInputSchema = z.object({ scriptId: scriptIdSchema }).strict();
export const scriptDeleteOutputSchema = z.object({ ok: z.literal(true) });

const scriptRunBaseSchema = z
  .object({
    scriptId: scriptIdSchema.optional(),
    scriptName: scriptNameSchema.optional(),
    body: scriptBodySchema.optional(),
    input: jsonValueSchema,
  })
  .strict();

export const scriptRunInputSchema = scriptRunBaseSchema.refine(
  (value) => [value.scriptId, value.scriptName, value.body].filter((part) => part !== undefined).length === 1,
  { message: 'Provide exactly one of scriptId, scriptName or body', path: ['scriptId'] }
);
export type ScriptRunInput = z.infer<typeof scriptRunInputSchema>;

export const scriptRunByIdBodySchema = z.object({ input: jsonValueSchema }).strict();
export type ScriptRunByIdBody = z.input<typeof scriptRunByIdBodySchema>;
export const scriptRunAdHocBodySchema = z.object({ body: scriptBodySchema, input: jsonValueSchema }).strict();
export type ScriptRunAdHocBody = z.input<typeof scriptRunAdHocBodySchema>;

export interface ScriptRef {
  id: number;
  name: string;
}

export const scriptRefSchema = z.object({ id: scriptIdSchema, name: z.string() });

export type ScriptRunOutcome = ScriptRunResult & { script: ScriptRef | null };

export const scriptRunOutputSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    output: jsonValueSchema,
    logs: z.array(z.string()),
    durationMs: z.number().int().nonnegative(),
    script: scriptRefSchema.nullable(),
  }),
  z.object({
    ok: z.literal(false),
    error: z.object({ name: z.string(), message: z.string() }),
    logs: z.array(z.string()),
    durationMs: z.number().int().nonnegative(),
    script: scriptRefSchema.nullable(),
  }),
]);
